import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Table, TableBody, TableHeader } from '@/components/ui/table';
import {
  PlannedResource,
  useUpdateWorkAssignmentListAttachment,
} from 'components/IAP/hooks/assignmentListHooks';
import AssignmentCardPlannedResourceEdit from './AssignmentCardPlannedResourceEdit';
import AssignmentCardResourceDialog, {
  type ResourceSelection,
} from './AssignmentCardResourceDialog';
import AssignmentCardRosterDialog from './AssignmentCardRosterDialog';
import ResourceSectionAddButtons from './ResourceSectionAddButtons';
import ResourceSectionTableHeader from './ResourceSectionTableHeader';
import { ICS204_TABLE } from './assignmentCardStyles';
import {
  emptyPlannedResourceDraft,
  plannedResourceFromDraft,
  type AssignmentCardFormData,
  type PlannedResourceDraft,
} from './assignmentCardFormTypes';
import { consumePendingDocumentationEntry } from './pendingDocumentationEntry';

/**
 * Section 5 in edit mode. Rows come from the form's `resource` record rather
 * than from the server row, so resources the user just picked show up before
 * the card is saved. Membership adds still PUT immediately so Cancel cannot
 * drop newly attached resources.
 */
const AssignmentCardResourceSectionEdit = ({
  walaId,
  periodNumber,
}: {
  walaId?: string | null;
  periodNumber?: number;
}) => {
  const { control, getValues, setValue, unregister } =
    useFormContext<AssignmentCardFormData>();
  const { mutateAsync: updateWala } = useUpdateWorkAssignmentListAttachment();

  const resourceMap = useWatch({ control, name: 'resource' });
  const resourceIds = Object.keys(resourceMap ?? {});

  const [showResourceModal, setShowResourceModal] = useState(false);
  const [showRosterModal, setShowRosterModal] = useState(false);

  // Both pickers seed their checkboxes from what is currently planned, which in
  // edit mode means the unsaved buffer rather than the server's list.
  const bufferedResources = resourceIds.map((id) => {
    const draft = resourceMap?.[id];
    return {
      id,
      isDocumentationEntry: !!draft?.isDocumentationEntry,
      documentationName: draft?.isDocumentationEntry
        ? draft.resourceName
        : null,
    } as PlannedResource;
  });

  const persistPlannedResources = (
    next: Record<string, PlannedResourceDraft>
  ) => {
    if (!walaId) return;
    const plannedResources = Object.entries(next).map(([id, res]) =>
      plannedResourceFromDraft(id, res)
    );
    void updateWala({
      walaId,
      plannedResources,
      touch: true,
    });
  };

  const prependDocumentationEntry = () => {
    const current = getValues('resource') ?? {};
    const id = crypto.randomUUID();
    const next: Record<string, PlannedResourceDraft> = {
      [id]: emptyPlannedResourceDraft('', { isDocumentationEntry: true }),
      ...current,
    };
    setValue('resource', next, { shouldDirty: true });
    persistPlannedResources(next);
  };

  useEffect(() => {
    if (consumePendingDocumentationEntry()) {
      prependDocumentationEntry();
    }
    // Only on mount: view-mode add sets the pending flag then enters edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only
  }, []);

  const commitSelection = (selection: ResourceSelection[]) => {
    const current = getValues('resource') ?? {};
    const next: Record<string, PlannedResourceDraft> = {};

    // Newly picked resources render first; kept drafts preserve in-session edits.
    // Documentation rows are not in the picker selection, so keep them at the front.
    const docs = Object.entries(current).filter(
      ([, draft]) => draft.isDocumentationEntry
    );
    for (const [id, draft] of docs) {
      next[id] = draft;
    }

    const newlyAdded = selection.filter(({ id }) => !current[id]);
    const kept = selection.filter(({ id }) => !!current[id]);
    for (const { id, name } of [...newlyAdded, ...kept]) {
      next[id] = current[id] ?? emptyPlannedResourceDraft(name);
    }

    // react-hook-form keeps values for fields that unmount, so a row dropped
    // from the table would otherwise still reach `persist`.
    for (const id of Object.keys(current)) {
      if (!next[id]) unregister(`resource.${id}`);
    }

    setValue('resource', next, { shouldDirty: true });
    persistPlannedResources(next);
  };

  return (
    <>
      <ResourceSectionAddButtons
        onAddResource={() => setShowResourceModal(true)}
        onAddFromRoster={() => setShowRosterModal(true)}
        onAddDocumentationEntry={prependDocumentationEntry}
      />
      {resourceIds.length !== 0 && (
        <div
          data-testid="ics204-edit-planned-resources"
          className="tw:min-w-0 tw:max-w-full tw:overflow-x-auto tw:overscroll-x-contain"
        >
          <Table className={ICS204_TABLE}>
            <TableHeader>
              <ResourceSectionTableHeader />
            </TableHeader>
            <TableBody>
              {resourceIds.map((id) => (
                <AssignmentCardPlannedResourceEdit
                  key={id}
                  resourceId={id}
                  periodNumber={periodNumber}
                  walaId={walaId}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {showResourceModal && (
        <AssignmentCardResourceDialog
          walaId={null}
          onClose={() => setShowResourceModal(false)}
          plannedResources={bufferedResources}
          onCommit={commitSelection}
        />
      )}
      {showRosterModal && (
        <AssignmentCardRosterDialog
          walaId={null}
          onClose={() => setShowRosterModal(false)}
          plannedResources={bufferedResources}
          onCommit={commitSelection}
        />
      )}
    </>
  );
};

export default AssignmentCardResourceSectionEdit;
